/**
 * `canary workflow proposals <verb>` — list / get / diff / accept / reject /
 * revert workflow proposals.
 *
 * Output mode auto-detects: JSON when piped (`!process.stdout.isTTY`), pretty
 * text when running interactively. `--json` and `--text` flags force either
 * mode. Designed so an agent can pipe JSON straight into `jq` without any
 * extra flag, and a human gets the readable report at a terminal.
 */

import process from 'node:process';
import {
  diffSnapshots,
  fromApiSnapshot,
  type SnapshotDiff,
} from '@chatsdet/workflow-diff';
import { getArgValue, hasFlag } from './auth.js';

/**
 * Like `getArgValue` but also accepts the `--key=value` form that many
 * agents emit by default. Falls back to the space-separated form. Matches
 * only exact `--key=` to avoid false-positives on `--status-filter=x` when
 * looking for `--status`.
 */
function getArg(argv: string[], key: string): string | undefined {
  const prefix = `${key}=`;
  const eq = argv.find((a) => a.startsWith(prefix));
  if (eq) return eq.slice(prefix.length);
  return getArgValue(argv, key);
}

/** @internal — exported for unit tests only. */
export const _internalsForTesting = { getArg };
import { apiRequest } from './cli-helpers.js';
import { renderProposalList, renderProposalDiff } from './proposals-format.js';

// ── Types ────────────────────────────────────────────────────────────────

export interface ProposalSummary {
  id: string;
  workflowId: string;
  title: string;
  description: string | null;
  source: 'agent' | 'user' | 'import' | 'api';
  status: 'pending' | 'accepted' | 'rejected' | 'stale';
  createdByUserId: string | null;
  decidedByUserId: string | null;
  decidedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProposalDetail extends ProposalSummary {
  snapshotJson: {
    nodes?: Array<{
      id: string;
      nodeType: string;
      orderIndex?: number;
      configJson?: Record<string, unknown>;
    }>;
    edges?: Array<{
      id: string;
      sourceNodeId: string;
      targetNodeId: string;
      edgeType?: string | null;
    }>;
  };
  sourceMetadata: Record<string, unknown> | null;
}

interface WorkflowDetail {
  id: string;
  nodes: Array<{
    id: string;
    nodeType: string;
    orderIndex?: number;
    configJson?: Record<string, unknown>;
  }>;
  edges: Array<{
    id: string;
    sourceNodeId: string;
    targetNodeId: string;
    edgeType?: string | null;
  }>;
  updatedAt?: string;
}

// ── Dispatcher ───────────────────────────────────────────────────────────

export async function handleWorkflowProposals(
  argv: string[],
  apiUrl: string,
  token: string,
): Promise<void> {
  const [verb, ...rest] = argv;
  switch (verb) {
    case 'list':
      await handleList(rest, apiUrl, token);
      break;
    case 'get':
      await handleGet(rest, apiUrl, token);
      break;
    case 'diff':
      await handleDiff(rest, apiUrl, token);
      break;
    case 'accept':
      await handleAccept(rest, apiUrl, token);
      break;
    case 'reject':
      await handleReject(rest, apiUrl, token);
      break;
    case 'revert':
      await handleRevert(rest, apiUrl, token);
      break;
    default:
      printHelp();
      process.exit(verb ? 1 : 0);
  }
}

function printHelp() {
  console.error(
    [
      'Usage: canary workflow proposals <verb> <workflowId> [<proposalId>] [options]',
      '',
      'Verbs:',
      '  list <wf>                    list proposals for a workflow',
      '  get <wf> <pid>               fetch a proposal (full snapshot)',
      '  diff <wf> <pid>              show the diff against the current draft',
      '  accept <wf> <pid>            apply the proposal (full or --select=id1,id2)',
      '  reject <wf> <pid>            reject the proposal (no draft change)',
      '  revert <wf> <pid>            revert a previously-accepted proposal to pending',
      '',
      'Global options:',
      '  --json                       force JSON output',
      '  --text                       force pretty-text output (default when stdout is a TTY)',
      '  --status=<status>            (list) filter by pending|accepted|rejected|stale|all',
      '  --include-unchanged          (diff) also list rows that did not change',
      '  --select=<ids>               (accept) comma-separated node ids to accept (default: all)',
    ].join('\n'),
  );
}

// ── Output-mode helper ───────────────────────────────────────────────────

function wantsJson(argv: string[]): boolean {
  if (hasFlag(argv, '--json')) return true;
  if (hasFlag(argv, '--text')) return false;
  return !process.stdout.isTTY;
}

function emit(argv: string[], textFallback: string, json: unknown): void {
  if (wantsJson(argv)) {
    console.log(JSON.stringify(json, null, 2));
  } else {
    console.log(textFallback);
  }
}

function requireArgs(
  argv: string[],
  n: number,
  verb: string,
): asserts argv is string[] {
  if (argv.length < n) {
    console.error(
      `Error: canary workflow proposals ${verb} requires ${n} argument${n === 1 ? '' : 's'}`,
    );
    printHelp();
    process.exit(1);
  }
}

// ── Handlers ─────────────────────────────────────────────────────────────

async function handleList(
  argv: string[],
  apiUrl: string,
  token: string,
): Promise<void> {
  requireArgs(argv, 1, 'list');
  const [workflowId] = argv;
  const status = getArg(argv, '--status') ?? 'pending';
  const query = status === 'all' ? '' : `?status=${encodeURIComponent(status)}`;

  const result = await apiRequest<{
    ok: boolean;
    data?: ProposalSummary[];
    error?: string;
  }>(apiUrl, token, 'GET', `/workflows/${workflowId}/proposals${query}`);

  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'failed to list proposals'}`);
    process.exit(1);
  }

  const proposals = result.data ?? [];
  emit(argv, renderProposalList(proposals), {
    workflowId,
    count: proposals.length,
    proposals,
  });
}

async function handleGet(
  argv: string[],
  apiUrl: string,
  token: string,
): Promise<void> {
  requireArgs(argv, 2, 'get');
  const [workflowId, proposalId] = argv;

  const result = await apiRequest<{
    ok: boolean;
    data?: ProposalDetail;
    error?: string;
  }>(apiUrl, token, 'GET', `/workflows/${workflowId}/proposals/${proposalId}`);

  if (!result.ok || !result.data) {
    console.error(`Error: ${result.error ?? 'proposal not found'}`);
    process.exit(1);
  }

  const prop = result.data;
  const text = [
    `Proposal: ${prop.title}`,
    `Source: ${prop.source}  Status: ${prop.status}`,
    `Created: ${prop.createdAt}`,
    `Nodes: ${prop.snapshotJson.nodes?.length ?? 0}  Edges: ${prop.snapshotJson.edges?.length ?? 0}`,
  ].join('\n');
  emit(argv, text, prop);
}

async function handleDiff(
  argv: string[],
  apiUrl: string,
  token: string,
): Promise<void> {
  requireArgs(argv, 2, 'diff');
  const [workflowId, proposalId] = argv;

  const [propRes, wfRes] = await Promise.all([
    apiRequest<{ ok: boolean; data?: ProposalDetail; error?: string }>(
      apiUrl,
      token,
      'GET',
      `/workflows/${workflowId}/proposals/${proposalId}`,
    ),
    apiRequest<{ ok: boolean; workflow?: WorkflowDetail; error?: string }>(
      apiUrl,
      token,
      'GET',
      `/workflows/${workflowId}`,
    ),
  ]);

  if (!propRes.ok || !propRes.data) {
    console.error(`Error: ${propRes.error ?? 'proposal not found'}`);
    process.exit(1);
  }
  if (!wfRes.workflow) {
    console.error(`Error: ${wfRes.error ?? 'workflow not found'}`);
    process.exit(1);
  }

  const proposal = propRes.data;
  const workflow = wfRes.workflow;

  const current = fromApiSnapshot({
    nodes: workflow.nodes,
    edges: workflow.edges,
  });
  const proposed = fromApiSnapshot(proposal.snapshotJson);
  const diff = diffSnapshots(current, proposed);

  const payload = buildDiffPayload(proposal, proposed, diff);
  const includeUnchanged = hasFlag(argv, '--include-unchanged');
  emit(argv, renderProposalDiff(payload, { includeUnchanged }), payload);
}

async function handleAccept(
  argv: string[],
  apiUrl: string,
  token: string,
): Promise<void> {
  requireArgs(argv, 2, 'accept');
  const [workflowId, proposalId] = argv;
  const selectArg = getArg(argv, '--select');
  const body: { selection?: { acceptedNodeIds: string[] } } = {};
  if (selectArg) {
    const ids = selectArg.split(',').map((s) => s.trim()).filter(Boolean);
    body.selection = { acceptedNodeIds: ids };
  }

  const result = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'POST',
    `/workflows/${workflowId}/proposals/${proposalId}/accept`,
    selectArg ? body : undefined,
  );

  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'accept failed'}`);
    process.exit(1);
  }

  emit(
    argv,
    selectArg
      ? `Accepted ${body.selection!.acceptedNodeIds.length} changes from ${proposalId}.`
      : `Accepted proposal ${proposalId} in full.`,
    { ok: true, proposalId, workflowId, partial: Boolean(selectArg) },
  );
}

async function handleReject(
  argv: string[],
  apiUrl: string,
  token: string,
): Promise<void> {
  requireArgs(argv, 2, 'reject');
  const [workflowId, proposalId] = argv;
  const result = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'POST',
    `/workflows/${workflowId}/proposals/${proposalId}/reject`,
  );
  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'reject failed'}`);
    process.exit(1);
  }
  emit(argv, `Rejected proposal ${proposalId}.`, {
    ok: true,
    proposalId,
    workflowId,
  });
}

async function handleRevert(
  argv: string[],
  apiUrl: string,
  token: string,
): Promise<void> {
  requireArgs(argv, 2, 'revert');
  const [workflowId, proposalId] = argv;
  const result = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'POST',
    `/workflows/${workflowId}/proposals/${proposalId}/revert`,
  );
  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'revert failed'}`);
    process.exit(1);
  }
  emit(argv, `Reverted proposal ${proposalId} back to pending.`, {
    ok: true,
    proposalId,
    workflowId,
  });
}

// ── Diff payload shaper ──────────────────────────────────────────────────

export interface DiffPayload {
  proposal: {
    id: string;
    title: string;
    source: string;
    status: string;
    stale: boolean;
    createdAt: string;
    decidedAt: string | null;
  };
  summary: {
    addedNodes: number;
    removedNodes: number;
    changedNodes: number;
    addedEdges: number;
    removedEdges: number;
    touchedCount: number;
    isEmpty: boolean;
  };
  nodes: {
    added: Array<{
      id: string;
      nodeType: string;
      configJson: Record<string, unknown>;
    }>;
    removed: Array<{
      id: string;
      nodeType: string;
      configJson: Record<string, unknown>;
    }>;
    changed: Array<{
      id: string;
      nodeType: string;
      typeChanged: boolean;
      beforeType?: string;
      afterType?: string;
      fieldChanges: Array<{ path: string; before: unknown; after: unknown }>;
    }>;
  };
  edges: {
    added: Array<{
      sourceNodeId: string;
      targetNodeId: string;
      edgeType: string | null;
    }>;
    removed: Array<{
      sourceNodeId: string;
      targetNodeId: string;
      edgeType: string | null;
    }>;
  };
}

function buildDiffPayload(
  proposal: ProposalDetail,
  proposedSnap: ReturnType<typeof fromApiSnapshot>,
  diff: SnapshotDiff,
): DiffPayload {
  return {
    proposal: {
      id: proposal.id,
      title: proposal.title,
      source: proposal.source,
      status: proposal.status,
      stale: proposal.status === 'stale',
      createdAt: proposal.createdAt,
      decidedAt: proposal.decidedAt,
    },
    summary: {
      addedNodes: diff.addedNodes.length,
      removedNodes: diff.removedNodes.length,
      changedNodes: diff.changedNodes.length,
      addedEdges: diff.addedEdges.length,
      removedEdges: diff.removedEdges.length,
      touchedCount: diff.touchedCount,
      isEmpty: diff.isEmpty,
    },
    nodes: {
      added: diff.addedNodes.map((n) => ({
        id: n.id,
        nodeType: n.nodeType,
        configJson: n.configJson,
      })),
      removed: diff.removedNodes.map((n) => ({
        id: n.id,
        nodeType: n.nodeType,
        configJson: n.configJson,
      })),
      changed: diff.changedNodes.map((c) => ({
        id: c.id,
        nodeType: c.nodeType,
        typeChanged: c.typeChanged,
        beforeType: c.beforeType,
        afterType: c.afterType,
        fieldChanges: c.fieldChanges,
      })),
    },
    edges: {
      added: diff.addedEdges.map((e) => ({
        sourceNodeId: e.sourceNodeId,
        targetNodeId: e.targetNodeId,
        edgeType: e.edgeType ?? null,
      })),
      removed: diff.removedEdges.map((e) => ({
        sourceNodeId: e.sourceNodeId,
        targetNodeId: e.targetNodeId,
        edgeType: e.edgeType ?? null,
      })),
    },
  };
}
